import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  addToAdQueue,
  getAdQueue,
  removeFromAdQueue,
  startAdBreak,
} from '../Apis/livestream/adQueue'
import { adQueueKey } from './useStreamState'

const NOTICE_MS = 6000
// A break the scheduler has not started by then is reported by the queue itself, not a spinner.
export const START_TIMEOUT_MS = 60_000

const EMPTY = { items: [], upNext: [] }

const NOTICES = {
  AD_QUEUE_EMPTY: 'Queue is empty',
  STREAM_NOT_RUNNING: 'Stream not live',
  AD_PLAYBACK_UNSUPPORTED: "This stream can't play videos",
}
// What stops the queue until Play is pressed again.
const STOPPED = {
  QueueStopped: 'Queue stopped',
  StreamNotLive: 'Stream not live',
  InternalError: 'Internal server error',
}

const noticeFor = (error) => {
  if (error?.response?.status === 404) return 'Video no longer exists'
  return NOTICES[error?.response?.data?.code] ?? "Couldn't update queue"
}

// One request's id; the same video can be added again while an earlier add is in flight.
const withoutOne = (ids, id) => {
  const index = ids.indexOf(id)
  return index === -1 ? ids : [...ids.slice(0, index), ...ids.slice(index + 1)]
}

/**
 * What a stream's break plays, and the operator's hold on it: add one of the event's videos, take a
 * queue entry off, start a break now. `upNext` is the whole lineup in play order, from what is on
 * air, Mismatch ads included and never named; the backend pushes each change over the stream's live
 * hub.
 *
 * `adding` holds the media ids and `removing` the entry ids with a request in flight. `starting` stays on from
 * Play until something goes on air (`onAir`), the request is refused, or START_TIMEOUT_MS passes.
 * `notice` carries a refusal, or a video the stream could not play, for a few seconds. `stopped` says why
 * the queue stopped, until Play is pressed again. `autoBreakAt` is when an automatic ad break is due
 * (epoch ms), or null; Play still takes over.
 */
const useStreamAdQueue = (orgId, streamId, enabled, onAir) => {
  const queryClient = useQueryClient()
  const key = adQueueKey(streamId)

  const { data = EMPTY } = useQuery(key, () => getAdQueue(orgId, streamId), {
    enabled: Boolean(enabled && orgId && streamId),
    refetchOnWindowFocus: true,
    retry: false,
  })

  // A backend from before the lineup sends the queue alone.
  const queue = { items: data.items ?? [], upNext: data.upNext ?? [] }

  // The backend counts the seconds; a new payload restarts the local countdown from them. react-query
  // keeps `data` for a payload identical to the last one, which leaves the count running.
  const autoBreakAt = useMemo(() => {
    const startsIn = data.autoBreak?.startsInSeconds
    return startsIn == null ? null : Date.now() + startsIn * 1000
  }, [data])

  const [notice, setNotice] = useState(null)
  useEffect(() => {
    if (!notice) return undefined
    const id = setTimeout(() => setNotice(null), NOTICE_MS)
    return () => clearTimeout(id)
  }, [notice])

  // Each notice from the stream has its own id, and is shown once.
  const streamNotice = data.notice ?? null
  const shownNotice = useRef(null)
  useEffect(() => {
    if (!streamNotice || streamNotice.id === shownNotice.current) return
    shownNotice.current = streamNotice.id
    if (streamNotice.kind === 'CouldNotPlay')
      setNotice(`Could not play ${streamNotice.filename ?? 'video'}`)
  }, [streamNotice])
  const stopped = STOPPED[streamNotice?.kind] ?? null

  const refuse = (error) => {
    setNotice(noticeFor(error))
    if (error?.response?.status === 404) queryClient.invalidateQueries(key)
  }

  const [adding, setAdding] = useState([])
  const add = useMutation((mediaId) => addToAdQueue(orgId, streamId, mediaId), {
    onMutate: (mediaId) => setAdding((ids) => [...ids, mediaId]),
    onSuccess: (next) => queryClient.setQueryData(key, next),
    onError: refuse,
    onSettled: (_, __, mediaId) => setAdding((ids) => withoutOne(ids, mediaId)),
  })

  const [removing, setRemoving] = useState([])
  const remove = useMutation((entryId) => removeFromAdQueue(orgId, streamId, entryId), {
    onMutate: (entryId) => setRemoving((ids) => [...ids, entryId]),
    onError: refuse,
    onSettled: (_, __, entryId) => {
      setRemoving((ids) => withoutOne(ids, entryId))
      queryClient.invalidateQueries(key)
    },
  })

  const [starting, setStarting] = useState(false)
  // The notice Play was pressed over, which the stream clears a moment later.
  const startedOver = useRef(null)
  const start = useMutation(() => startAdBreak(orgId, streamId), {
    onMutate: () => {
      startedOver.current = streamNotice?.id ?? null
      setStarting(true)
    },
    onError: (error) => {
      setStarting(false)
      refuse(error)
    },
  })
  useEffect(() => {
    if (starting && onAir) setStarting(false)
  }, [starting, onAir])
  useEffect(() => {
    if (starting && streamNotice && streamNotice.id !== startedOver.current) setStarting(false)
  }, [starting, streamNotice])
  useEffect(() => {
    if (!starting) return undefined
    const id = setTimeout(() => setStarting(false), START_TIMEOUT_MS)
    return () => clearTimeout(id)
  }, [starting])

  return {
    queue,
    autoBreakAt,
    add: add.mutate,
    remove: remove.mutate,
    start: start.mutate,
    adding,
    removing,
    starting,
    notice,
    stopped: starting ? null : stopped,
  }
}

export default useStreamAdQueue
